import { sendAnalyticsEvent, UnknownEvent } from '@faststore/sdk'
import { useEffect, useRef, useState } from 'react'
import { ArrowHeaderDown } from 'src/components/icons/ArrowHeaderDown'
import { useOnClickAway } from 'src/sdk/ui/useOnClickAway'
import type { MenuItem } from '../../../Menu/global-navigation'

type SelectedItem = {
  src?: string | null
  href: string | null
  path?: string
}

type DropdownProps = {
  dropdownMenu: MenuItem
}

export const DropdownMenuItem = ({ dropdownMenu }: DropdownProps) => {
  const [openDropdown, setOpenDropdown] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [selectedItem, setSelectedItem] = useState<SelectedItem>(
    dropdownMenu.nextLevel?.items?.[0]?.subHeaderImage !== undefined
      ? {
          src: `url(${dropdownMenu.nextLevel?.items?.[0]?.subHeaderImage})`,
          href: dropdownMenu.nextLevel?.items?.[0]?.labelURL ?? '',
          path: dropdownMenu.nextLevel?.items?.[0]?.label ?? '',
        }
      : {
          src: '',
          href: dropdownMenu.nextLevel?.items?.[0]?.labelURL ?? '',
          path: dropdownMenu.nextLevel?.items?.[0]?.label ?? '',
        }
  )
  const [hasMounted, setHasMounted] = useState(false)

  const handleAnalytics = () => {
    if (!dropdownMenu.title) return

    sendAnalyticsEvent<UnknownEvent>({
      name: 'menu_categorie',
      params: {
        path: selectedItem.path ?? '',
        section: 'subheader',
        main_category: dropdownMenu.title,
      },
    })
  }

  const ref = useRef<HTMLDivElement>(null)

  const handleClick = () => {
    if (!openDropdown) {
      const defaultItem =
        dropdownMenu.nextLevel?.items?.[0]?.subHeaderImage !== undefined
          ? {
              src: dropdownMenu.nextLevel?.items[0]?.subHeaderImage,
              href: dropdownMenu.nextLevel?.items[0]?.labelURL ?? '',
              path: dropdownMenu.nextLevel?.items[0]?.label ?? '',
            }
          : {
              src: '',
              href: dropdownMenu.nextLevel?.items?.[0]?.labelURL ?? '',
              path: dropdownMenu.nextLevel?.items[0]?.label ?? '',
            }
      handleSelectItem(defaultItem, 0)
    } else {
      if (!dropdownMenu.title) return
      sendAnalyticsEvent<UnknownEvent>({
        name: 'menu_categorie',
        params: {
          path: '',
          section: 'subheader',
          main_category: dropdownMenu.title,
        },
      })
    }
    setOpenDropdown(!openDropdown)
  }

  const handleClickAwayCallback = () => {
    dropdownMenu.title &&
      sendAnalyticsEvent<UnknownEvent>({
        name: 'menu_categorie',
        params: {
          path: '',
          section: 'subheader',
          main_category: dropdownMenu.title,
        },
      })
    setOpenDropdown && setOpenDropdown(false)
  }

  useOnClickAway({
    isOpened: openDropdown,
    ref,
    callback: handleClickAwayCallback,
  })

  const handleSelectItem = (item: SelectedItem, index?: number) => {
    const selectedItem = {
      src: `url(${item.src ?? ''})`,
      href: item.href ?? '',
      path: item.path,
    }
    if (typeof index === 'number') setSelectedIndex(index)
    setSelectedItem(selectedItem)
  }

  useEffect(() => {
    setHasMounted(true)
  }, [])

  return (
    <div className="dropdown-subheader-component" ref={ref}>
      {dropdownMenu.nextLevel?.items?.length > 0 ? (
        <button
          className={`flex flex-row items-center gap-1 text-xs font-normal tracking-[0.048px] !leading-[130%] dropdown-subheader-link cursor-pointer bg-white ${
            openDropdown ? 'open' : ''
          }`}
          style={{ color: dropdownMenu.titleColor ?? '' }}
          onClick={handleClick}
        >
          <span className="block">{dropdownMenu.title}</span>
          <ArrowHeaderDown />
        </button>
      ) : (
        <a
          className="text-xs font-normal tracking-[0.048px] !leading-[130%] dropdown-subheader-link cursor-pointer"
          href={dropdownMenu.titleURL ?? ''}
          onClick={() => handleAnalytics()}
        >
          {dropdownMenu.title}
        </a>
      )}
      <div className="absolute right-10 top-[52px] max-w-[calc(100vw-80px)] min-h-[216px] opacity-0 -z-1 flex flex-row bg-white rounded-lg shadow-[0px_0px_16px_0px_rgba(0,0,0,0.2)] overflow-hidden [&amp;>*:nth-child(n+2)]:border-l-2 [&amp;>*:nth-child(n+2)]:border-[#E2E2E2] [&amp;>*:nth-child(n+2)]:border-solid [&amp;>*:last-child]:!border-none dropdown-subheader-body">
        <div className="py-2 min-w-[150px]">
          {dropdownMenu.nextLevel?.items?.map((item, index: number) => {
            const selectedItem = {
              src: item.subHeaderImage,
              href: item.labelURL,
              path: `${item.label}`,
            }
            return (
              <a
                href={item.labelURL ?? undefined}
                target={item.openInNewTab ? '_blank' : undefined}
                rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                key={`dropmenu-el-${index}`}
                className={`no-underline text-black block w-full text-xs !leading-[130%] tracking-[0.048px] px-4 py-2 transition-all hover:bg-yellow hover:font-bold hover:rounded-none cursor-pointer ${
                  selectedIndex === index
                    ? '!bg-yellow !font-bold !rounded-none'
                    : ''
                }`}
                onMouseEnter={() => handleSelectItem(selectedItem, index)}
                onClick={() => handleAnalytics()}
              >
                {item.label}
              </a>
            )
          })}
        </div>
        {dropdownMenu.nextLevel?.items?.find(
          (el) =>
            el?.nextLevel?.items !== null && el?.nextLevel?.items?.length !== 0
        ) && (
          <div className="py-2 min-w-[225px]">
            {dropdownMenu.nextLevel?.items?.[
              selectedIndex
            ]?.nextLevel?.items?.map((thirdLevelItem, index: number) => {
              const selectedItem = {
                src: thirdLevelItem.subHeaderImage,
                href: thirdLevelItem.labelURL,
                path: `${dropdownMenu.nextLevel?.items?.[selectedIndex]?.label}||${thirdLevelItem.label}`,
              }
              return (
                <a
                  key={`dropmenu-third-level-${index}`}
                  href={thirdLevelItem.labelURL ?? undefined}
                  target={thirdLevelItem.openInNewTab ? '_blank' : undefined}
                  rel={
                    thirdLevelItem.openInNewTab
                      ? 'noopener noreferrer'
                      : undefined
                  }
                  onClick={() => handleAnalytics()}
                  onMouseEnter={() => handleSelectItem(selectedItem)}
                  className="block text-black no-underline w-full text-xs !leading-[130%] tracking-[0.048px] px-4 py-2 font-normal transition-all hover:bg-yellow hover:font-bold hover:text-black hover:rounded-none cursor-pointer"
                >
                  {thirdLevelItem.label}
                </a>
              )
            })}
          </div>
        )}
        <a
          href={selectedItem.href ? selectedItem.href : undefined}
          className="flex"
          onClick={() => handleAnalytics()}
          aria-label="Scopri la promo!"
        >
          {hasMounted && (
            <div
              className="w-[290px] bg-center bg-no-repeat bg-cover"
              style={{
                backgroundImage: selectedItem.src ?? '',
              }}
            ></div>
          )}
        </a>
      </div>
    </div>
  )
}
